import * as Yup from 'yup';
import { Path } from 'react-hook-form';
import { TransportOrderFormValues } from './types';
import { isBackendResolvedPalletType } from './palletDimensions';

const requiredString = (label: string) =>
  Yup.string()
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .required(`${label} is required`);

const requiredNumberLike = (label: string) =>
  Yup.mixed().test(`${label}-required`, `${label} is required`, (value) => {
    if (value === undefined || value === null || value === '') return false;
    const n = Number(value);
    return !Number.isNaN(n) && n > 0;
  });

const shipmentMethodWhenRequired = Yup.string().when(
  '$isShipmentMethodRequired',
  {
    is: true,
    then: (schema) => schema.required('Choose a shipment method'),
    otherwise: (schema) => schema,
  }
);

// Scheduling constraints surfaced from the despatch note (US-005). All fields are
// "HH:mm" strings so lexical comparison doubles as chronological.
export interface ScheduleConstraints {
  dispatchCutoffTime?: string | null; // warehouse latest pickup time
  customerOperatingHoursFrom?: string | null; // customer opens at
  customerOperatingHoursTo?: string | null; // customer closes at
}

// Step 1 schema factory — when prepped from a despatch note, load fields are
// populated from the server; in standalone mode the user must supply them.
function buildStep1Schema(
  isFromDespatchNote: boolean,
  schedule?: ScheduleConstraints
) {
  const cutoff = schedule?.dispatchCutoffTime ?? null;
  const openFrom = schedule?.customerOperatingHoursFrom ?? null;
  const openTo = schedule?.customerOperatingHoursTo ?? null;

  return Yup.object().shape({
    // Locations
    collectionPostcode: requiredString('Collection postcode'),
    deliveryPostcode: requiredString('Delivery postcode'),

    // Timing
    collectionDate: requiredString('Collection date'),
    collectionTimeFrom: requiredString('Collection time from'),
    // Collection must end by the warehouse's dispatch cutoff (HH:mm strings compare
    // lexically as chronological in 24h format).
    collectionTimeTo: requiredString('Collection time to').test(
      'cutoff',
      cutoff
        ? `Collection must end by warehouse cutoff (${cutoff})`
        : 'Collection time to is required',
      (value) => {
        if (!cutoff || !value) return true;
        return value <= cutoff;
      }
    ),
    deliveryDate: requiredString('Delivery date'),
    // Delivery window must fit within the customer's operating hours when declared.
    deliveryTimeFrom: requiredString('Delivery time from').test(
      'openFrom',
      openFrom
        ? `Delivery must start at or after customer opening (${openFrom})`
        : 'Delivery time from is required',
      (value) => {
        if (!openFrom || !value) return true;
        return value >= openFrom;
      }
    ),
    deliveryTimeTo: requiredString('Delivery time to').test(
      'openTo',
      openTo
        ? `Delivery must end at or before customer closing (${openTo})`
        : 'Delivery time to is required',
      (value) => {
        if (!openTo || !value) return true;
        return value <= openTo;
      }
    ),
    // earliestCollectionTime / deadlineType are not currently rendered in the UI
    // but remain on the form values for future use. Optional so they never block
    // Continue from step 1.
    earliestCollectionTime: Yup.string().optional(),
    deadlineType: Yup.string().oneOf(['time-critical', 'flexible']).optional(),
    // Booking reference is auto-filled from the despatch note in SCP mode; in standalone
    // mode the user can leave it blank and add it later on commit.
    bookingReference: Yup.string().optional(),

    carrier: Yup.string().optional(),
    shipmentMethod: shipmentMethodWhenRequired,

    // Dimensions and Weight
    totalPallets: isFromDespatchNote
      ? Yup.mixed().optional()
      : requiredNumberLike('Total pallets'),
    palletType: isFromDespatchNote
      ? Yup.string().optional()
      : requiredString('Pallet type'),
    stackable: Yup.boolean().required('Stackable is required'),
    briefContents: requiredString('Brief contents'),
    height: isFromDespatchNote
      ? Yup.mixed().optional()
      : requiredNumberLike('Max pallet height'),
    heightUnit: requiredString('Height unit'),
    // Pallet footprint: the 4 backend-resolved types (GB/EU + halves) are filled
    // server-side, so length/width stay optional (and the FE hides the inputs). Every
    // other type — Custom Pallet / Other / Unpalletised / Mixed GB/EU — must be declared
    // by the user in standalone mode; the engine has no footprint for them.
    palletLength: isFromDespatchNote
      ? Yup.mixed().optional()
      : Yup.mixed().when('palletType', {
          is: (val: string) => !!val && !isBackendResolvedPalletType(val),
          then: () => requiredNumberLike('Pallet length'),
          otherwise: () => Yup.mixed().optional(),
        }),
    palletWidth: isFromDespatchNote
      ? Yup.mixed().optional()
      : Yup.mixed().when('palletType', {
          is: (val: string) => !!val && !isBackendResolvedPalletType(val),
          then: () => requiredNumberLike('Pallet width'),
          otherwise: () => Yup.mixed().optional(),
        }),
    weight: isFromDespatchNote
      ? Yup.mixed().optional()
      : requiredNumberLike('Weight'),
    weightUnit: requiredString('Weight unit'),
    overhang: Yup.boolean().required('Overhang is required'),
    overhangDescription: Yup.string().when('overhang', {
      is: true,
      then: (schema) => schema.required('Overhang description is required'),
      otherwise: (schema) => schema,
    }),
    packaging: isFromDespatchNote
      ? Yup.string().optional()
      : requiredString('Packaging'),

    // Surcharges — optional extras the pallet networks price into the quote.
    amDeliveryRequired: Yup.boolean(),
    pmDeliveryRequired: Yup.boolean(),
    timedDeliveryRequired: Yup.boolean(),
    saturdayAmDeliveryRequired: Yup.boolean(),

    preferredVehicle: Yup.string().optional(),
    curtainSideOrBoxBody: Yup.string().optional(),
    tailLiftRequired: Yup.boolean().required('Tail-lift required is required'),
    moffettRequired: Yup.boolean().required(
      'Moffett/forklift required is required'
    ),
    minimumInternalHeight: Yup.mixed().optional(),
    minimumInternalHeightUnit: Yup.string().optional(),

    // Enhanced Liability Cover — optional for quoting; captured for the order.
    fragileHandling: Yup.boolean(),
    insuranceCover: Yup.string(),
    insuranceCurrency: Yup.string(),

    // Loading/Unloading on Site
    forkliftAtCollection: Yup.boolean().required(
      'Forklift at collection is required'
    ),
    forkliftAtDelivery: Yup.boolean().required(
      'Forklift at delivery is required'
    ),
    dockLoading: Yup.boolean().required('Dock loading is required'),
    handballRequired: Yup.boolean().required('Handball required is required'),
    palletTruckNeeded: Yup.boolean().required(
      'Pallet truck needed is required'
    ),
  });
}

const quoteSelectionWhenRequired = Yup.string().when(
  '$isQuoteSelectionRequired',
  {
    is: true,
    then: (schema) => schema.required('Select a quote to continue'),
    otherwise: (schema) => schema,
  }
);

// Step 2: Collection → Delivery addresses + quote selection.
// Addresses are all optional — contacts are captured for the final transport order
// but are NOT prerequisites for advancing. A quote is required whenever quotes are
// offered or still loading; with none offered the order takes the manual-quote path.
const step2Schema = Yup.object().shape({
  selectedRateCardId: quoteSelectionWhenRequired,
  pickupCompanyName: Yup.string(),
  pickupFullAddress: Yup.string(),
  pickupPostcode: Yup.string(),
  pickupContactName: Yup.string(),
  pickupContactNumber: Yup.string(),
  pickupContactEmail: Yup.string().email('Invalid email address'),
  pickupAdditionalNotes: Yup.string(),

  dropoffCompanyName: Yup.string(),
  dropoffFullAddress: Yup.string(),
  dropoffPostcode: Yup.string(),
  dropoffContactName: Yup.string(),
  dropoffContactNumber: Yup.string(),
  dropoffContactEmail: Yup.string().email('Invalid email address'),
  dropoffAdditionalNotes: Yup.string(),
});

// Step 3: read-only summary. Re-checks the quote selection so a refetch that cleared
// it after the user reached confirmation still blocks submit.
const confirmationSchema = Yup.object().shape({
  selectedRateCardId: quoteSelectionWhenRequired,
});

// Schema factory: produces step-indexed schemas with conditional required-ness
// for load fields based on whether the form is prepped from a despatch note.
export function buildSchemas(
  isFromDespatchNote: boolean,
  schedule?: ScheduleConstraints
) {
  const step1 = buildStep1Schema(isFromDespatchNote, schedule);
  return [step1, step2Schema, confirmationSchema];
}

// Convenience: full schema for a given mode (used for final submission resolver)
export function buildFullSchema(
  isFromDespatchNote: boolean,
  schedule?: ScheduleConstraints
) {
  return buildStep1Schema(isFromDespatchNote, schedule)
    .concat(step2Schema)
    .concat(confirmationSchema);
}

// Step field mapping for FormPage validation (1-indexed to match currentStep)
export const stepFields: Record<number, Path<TransportOrderFormValues>[]> = {
  1: [
    // Locations
    'collectionPostcode',
    'deliveryPostcode',
    // Timing
    'collectionDate',
    'collectionTimeFrom',
    'collectionTimeTo',
    'deliveryDate',
    'deliveryTimeFrom',
    'deliveryTimeTo',
    'earliestCollectionTime',
    'deadlineType',
    'bookingReference',
    // Carrier and Shipment Method
    'carrier',
    'shipmentMethod',
    // Dimensions and Weight
    'totalPallets',
    'palletType',
    'stackable',
    'briefContents',
    'height',
    'heightUnit',
    'palletLength',
    'palletWidth',
    'weight',
    'weightUnit',
    'overhang',
    'overhangDescription',
    'packaging',
    // Surcharges
    'amDeliveryRequired',
    'pmDeliveryRequired',
    'timedDeliveryRequired',
    'saturdayAmDeliveryRequired',

    'preferredVehicle',
    'curtainSideOrBoxBody',
    'tailLiftRequired',
    'moffettRequired',
    'minimumInternalHeight',
    'minimumInternalHeightUnit',
    // Loading/Unloading on Site
    'forkliftAtCollection',
    'forkliftAtDelivery',
    'dockLoading',
    'handballRequired',
    'palletTruckNeeded',
    // Enhanced Liability Cover
    'fragileHandling',
    'insuranceCover',
    'insuranceCurrency',
  ],
  2: [
    'selectedRateCardId',
    'pickupCompanyName',
    'pickupFullAddress',
    'pickupPostcode',
    'pickupContactName',
    'pickupContactNumber',
    'pickupContactEmail',
    'pickupAdditionalNotes',
    'dropoffCompanyName',
    'dropoffFullAddress',
    'dropoffPostcode',
    'dropoffContactName',
    'dropoffContactNumber',
    'dropoffContactEmail',
    'dropoffAdditionalNotes',
  ],
  3: [],
};
